import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, Calculator, ArrowLeft } from "lucide-react";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ChatState {
  session_id: string;
  current_step: string;
  [key: string]: any;
}

interface LangGraphChatProps {
  onBack?: () => void;
}

export const LangGraphChat: React.FC<LangGraphChatProps> = ({ onBack }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatState, setChatState] = useState<ChatState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start conversation
    startConversation();
  }, []);

  const startConversation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.request<{
        message: string;
        session_id: string;
        state: ChatState;
        current_step: string;
      }>(EndpointKeys.SALARY_CALCULATOR_LANGGRAPH_CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          message: "",
          session_id: `session_${Date.now()}`,
        },
        skipAuth: true,
      });

      setMessages([
        {
          role: "assistant",
          content: response.message,
          timestamp: new Date(),
        },
      ]);

      setChatState(response.state);
      
      // Auto-trigger calculation if we start in calculate_salary step
      if (response.current_step === "calculate_salary") {
        setTimeout(() => {
          triggerCalculation(response.state, response.session_id);
        }, 500);
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      setError(
        error instanceof ApiError
          ? error.message
          : "Failed to start conversation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const triggerCalculation = async (currentState: ChatState, sessionId: string) => {
    setLoading(true);
    try {
      const response = await ApiService.request<{
        message: string;
        session_id: string;
        state: ChatState;
        current_step: string;
      }>(EndpointKeys.SALARY_CALCULATOR_LANGGRAPH_CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          message: "", // Empty message to trigger calculation
          session_id: sessionId,
          state: currentState,
        },
        skipAuth: true,
      });

      const assistantMessage: Message = {
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setChatState(response.state);
    } catch (error) {
      console.error("Error triggering calculation:", error);
      const errorMessage: Message = {
        role: "assistant",
        content: "I'm having trouble with the calculation. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = input;
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.request<{
        message: string;
        session_id: string;
        state: ChatState;
        current_step: string;
      }>(EndpointKeys.SALARY_CALCULATOR_LANGGRAPH_CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          message: currentInput,
          session_id: chatState?.session_id,
          state: chatState,
        },
        skipAuth: true,
      });

      const assistantMessage: Message = {
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setChatState(response.state);
      
      // Auto-trigger calculation if we're in calculate_salary step
      if (response.current_step === "calculate_salary") {
        setTimeout(() => {
          triggerCalculation(response.state, response.session_id);
        }, 500); // Small delay for better UX
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: Message = {
        role: "assistant",
        content: "I'm having trouble processing that. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setError(
        error instanceof ApiError
          ? error.message
          : "Failed to send message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setChatState(null);
    setError(null);
    startConversation();
  };

  if (error && messages.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-4 bg-white">
        <Card className="w-full max-w-2xl mx-auto">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <MessageCircle className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-4">Connection Error</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex gap-4">
              <Button onClick={resetConversation}>Try Again</Button>
              {onBack && (
                <Button variant="outline" onClick={onBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center min-h-screen p-4 bg-white">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h1 className="text-3xl font-bold text-primary">Hirelo</h1>
          </div>
          <Button variant="outline" size="sm" onClick={resetConversation}>
            New Calculation
          </Button>
        </div>

        {/* Chat Container */}
        <Card className="w-full h-[700px] flex flex-col bg-slate-50">
          <div className="flex items-center gap-3 p-4 border-b bg-white rounded-t-lg">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
              <Calculator className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold">TaxBot</h2>
              <p className="text-sm text-muted-foreground">
                UK Salary Calculator Assistant
              </p>
            </div>
          </div>

          {/* Messages Area - Fixed height with proper scrolling */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[85%] p-4 rounded-2xl whitespace-pre-wrap ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground ml-12"
                          : "bg-white text-foreground mr-12 shadow-sm border"
                      }`}
                    >
                      <div className="text-sm leading-relaxed">
                        {message.content}
                      </div>
                      <div
                        className={`text-xs mt-2 opacity-70 ${
                          message.role === "user" ? "text-right" : "text-left"
                        }`}
                      >
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white p-4 rounded-2xl shadow-sm border mr-12">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                          <div
                            className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
                            style={{ animationDelay: "0.1s" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
                            style={{ animationDelay: "0.2s" }}
                          ></div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          TaxBot is thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t bg-white rounded-b-lg">
            {error && (
              <div className="mb-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={loading}
                  className="min-h-[44px] resize-none border-2 focus:border-primary"
                />
              </div>
              <Button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                size="lg"
                className="h-[44px] px-4 bg-primary hover:bg-primary/90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">
                Press Enter to send, Shift+Enter for new line
              </p>
              {chatState?.current_step && (
                <p className="text-xs text-muted-foreground">
                  Step: {chatState.current_step.replace("_", " ")}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            © Hirelo {new Date().getFullYear()} • All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default LangGraphChat;
